import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { PageHeader, Field } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function Packages() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/packages").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...edit, xml_quota: Number(edit.xml_quota), price_eur: Number(edit.price_eur), storage_mb: Number(edit.storage_mb || 0) };
      if (edit.id) await api.put(`/admin/packages/${edit.id}`, body);
      else await api.post("/admin/packages", body);
      toast.success("Pacchetto salvato");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/packages/${id}`); toast.success("Pacchetto eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Pacchetti XML" subtitle="Spazio documenti da assegnare alle aziende clienti">
        <Button data-testid="add-package-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({ name: "", xml_quota: 100, price_eur: 0, description: "", storage_mb: 0 })}><Plus className="mr-2 h-4 w-4" />Nuovo pacchetto</Button>
      </PageHeader>
      <div className="stagger grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`package-card-${p.id}`} className="lift relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6">
            <div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-sky-50" />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">{p.name}</div>
              <div className="mt-3 font-mono text-4xl font-semibold tracking-tight">{p.xml_quota.toLocaleString("it-IT")}<span className="ml-1 text-sm text-slate-500">XML</span></div>
              <div className="mt-1 text-sm text-slate-600">{eur(p.price_eur)} · {p.companies} aziende</div>
              <div className="text-xs text-slate-500" data-testid={`package-storage-${p.id}`}>Spazio file: {p.storage_mb ? `${p.storage_mb.toLocaleString("it-IT")} MB` : "illimitato"}</div>
              <p className="mt-3 min-h-[2.5rem] text-sm text-slate-500">{p.description}</p>
              <div className="mt-4 flex gap-1">
                <Button variant="outline" size="sm" data-testid={`edit-package-${p.id}`} onClick={() => setEdit({ ...p })}><Pencil className="mr-1.5 h-3.5 w-3.5" />Modifica</Button>
                <ConfirmDelete testid={`delete-package-${p.id}`} onConfirm={() => del(p.id)} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nome" className="sm:col-span-2"><Input data-testid="package-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
              <Field label="Documenti XML inclusi"><Input data-testid="package-quota-input" type="number" value={edit.xml_quota} onChange={(e) => setEdit({ ...edit, xml_quota: e.target.value })} /></Field>
              <Field label="Spazio per i file caricati (MB, 0 = illimitato)"><Input data-testid="package-storage-input" type="number" min="0" value={edit.storage_mb ?? 0} onChange={(e) => setEdit({ ...edit, storage_mb: e.target.value })} /></Field>
              <Field label="Prezzo €"><Input data-testid="package-price-input" type="number" step="0.01" value={edit.price_eur} onChange={(e) => setEdit({ ...edit, price_eur: e.target.value })} /></Field>
              <Field label="Descrizione" className="sm:col-span-2"><Textarea data-testid="package-description-input" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter><Button data-testid="save-package-button" disabled={!edit?.name} onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
